/**
 * "6.000" → "6", "2.500" → "2.5". Leaves non-numbers as they are.
 *
 * @param {*} value
 * @return {string}
 */
export const trimQty = value => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : String(value);
};

/**
 * Plain-English problem with a typed quantity, or null when it is fine.
 *
 * @param {string} text
 * @param {*=} max Largest allowed quantity, if any.
 * @return {?string}
 */
export const qtyError = (text, max) => {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "Enter a quantity";
  const number = Number(trimmed);
  if (!Number.isFinite(number) || number <= 0) return "Enter more than 0";
  if (max !== undefined && max !== null && number > Number(max)) {
    return `No more than ${trimQty(max)}`;
  }
  return null;
};

/**
 * Sum of the quantities typed for the ticked rows.
 *
 * @param {!Set<*>} checked
 * @param {!Object<string, string>} quantities
 * @return {number}
 */
export const totalQty = (checked, quantities) =>
  [...checked].reduce((sum, id) => sum + (Number(quantities[id]) || 0), 0);

/** @const {!Object<string, {label: string, url: function(*): string}>} */
const CREATED_LINKS = {
  picklists: { label: "Pick list", url: id => `/picklist/${id}` },
  stock_packs: { label: "Pack", url: id => `/stock_packs/${id}` },
  deliveries: { label: "Delivery", url: id => `/delivery/${id}` },
  shipments: { label: "Shipment", url: id => `/shipment/${id}` },
};

/**
 * Links to the documents a step created, e.g. "Pick list PL-12".
 *
 * @param {?Object} created `created` block from a step response.
 * @return {!Array<{key: string, label: string, to: string}>}
 */
export const createdLinks = created => {
  if (!created) return [];
  const links = [];
  Object.entries(CREATED_LINKS).forEach(([key, config]) => {
    (created[key] || []).forEach(doc => {
      links.push({
        key: `${key}:${doc.id}`,
        label: `${config.label} ${doc.name || `#${doc.id}`}`,
        to: config.url(doc.id),
      });
    });
  });
  if (created.invoice?.id) {
    links.push({
      key: `invoice:${created.invoice.id}`,
      label: `Invoice ${created.invoice.name || `#${created.invoice.id}`}`,
      to: `/sale_invoice/${created.invoice.id}`,
    });
  }
  return links;
};

/**
 * "Line 3 · SKU-123" for result and skipped lists.
 *
 * @param {{line_number?: ?string, product_number?: ?string}} item
 * @return {string}
 */
export const describeItem = item =>
  [item.line_number && `Line ${item.line_number}`, item.product_number].filter(Boolean).join(" · ");

/**
 * Reads the message out of an RTK Query error for a whole-step failure.
 *
 * @param {*} error
 * @return {string}
 */
export const stepErrorMessage = error => {
  const data = error?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (error?.status === "FETCH_ERROR") {
    return "We couldn't reach Orderlink. Check your connection and try again.";
  }
  if (error?.status === 404) {
    return "This step isn't available on the server yet. Update and restart the backend, then try again.";
  }
  if (data && typeof data === "object") {
    const first = Object.values(data)
      .flat()
      .find(value => typeof value === "string");
    if (first) return first;
  }
  return "Something went wrong. Please try again.";
};
